import { useEffect, useMemo, useState } from 'react'
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

import { createSetupIntent } from '../../utilities/api'

import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import Form from 'react-bootstrap/Form'

import { Spinner } from '../LoadingContainer/Spinner'

function SetupPaymentMethodForm({
  onSuccess,
  closeModal,
  replacementPaymentMethodId,
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!stripe || !elements || isSaving) return
    setError('')
    setIsSaving(true)
    try {
      const payload = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      })
      if (payload?.error) {
        throw new Error(
          payload.error?.message || 'Unable to save payment method'
        )
      }
      const paymentMethodId = payload?.setupIntent?.payment_method
      await onSuccess({
        paymentMethodId,
        replacementPaymentMethodId,
      })
      closeModal()
    } catch (err) {
      setError(err?.message || 'Unable to save payment method')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal.Body>
      <div className="modal-body-heading">
        <h1 className="modal-body-heading-title">
          {replacementPaymentMethodId ? 'Replace card' : 'Update card'}
        </h1>
        <p>
          Card details are securely handled by Stripe. No charge is made while
          updating this card.
        </p>
      </div>
      <Form onSubmit={handleSubmit}>
        <PaymentElement />
        {error ? (
          <Alert variant="danger" className="mt-3 mb-0">
            {error}
          </Alert>
        ) : null}
        <Stack className="btn-group-flex mt-4">
          <Button type="submit" size="lg" disabled={!stripe || isSaving}>
            {isSaving ? <Spinner variant="white" /> : 'Save card'}
          </Button>
          <Button
            type="button"
            size="lg"
            variant="outline-light"
            onClick={closeModal}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </Stack>
      </Form>
    </Modal.Body>
  )
}

export function AddPaymentMethodModal({
  stripeCustomerId,
  connectAccountId,
  replacementPaymentMethodId,
  onSuccess,
  closeModal,
}) {
  const [isLoadingIntent, setIsLoadingIntent] = useState(true)
  const [setupIntentError, setSetupIntentError] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [stripeAccountContext, setStripeAccountContext] = useState(
    connectAccountId || null
  )

  useEffect(() => {
    setIsLoadingIntent(true)
    setSetupIntentError('')
    createSetupIntent({
      stripeCustomerId,
      connectAccountId,
    })
      .then((res) => {
        const nextSecret = res?.data?.clientSecret || ''
        if (!nextSecret) {
          throw new Error('Setup intent did not return a client secret')
        }
        setStripeAccountContext(res?.data?.stripeAccount || null)
        setClientSecret(nextSecret)
      })
      .catch((err) => {
        setSetupIntentError(err?.response?.data?.error?.message || err?.message)
      })
      .finally(() => {
        setIsLoadingIntent(false)
      })
  }, [stripeCustomerId, connectAccountId])

  const stripePromise = useMemo(
    () =>
      loadStripe(
        process.env.REACT_APP_STRIPE_KEY,
        stripeAccountContext
          ? { stripeAccount: stripeAccountContext }
          : undefined
      ),
    [stripeAccountContext]
  )

  if (isLoadingIntent) {
    return (
      <Modal.Body>
        <Stack className="align-items-center py-4">
          <Spinner />
        </Stack>
      </Modal.Body>
    )
  }

  if (setupIntentError || !clientSecret) {
    return (
      <Modal.Body>
        <div className="modal-body-heading">
          <h1 className="modal-body-heading-title">Unable to open card form</h1>
          <p>{setupIntentError || 'Setup intent is unavailable.'}</p>
        </div>
        <Button onClick={closeModal}>Close</Button>
      </Modal.Body>
    )
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <SetupPaymentMethodForm
        onSuccess={onSuccess}
        closeModal={closeModal}
        replacementPaymentMethodId={replacementPaymentMethodId}
      />
    </Elements>
  )
}
